/**
 * useDeleteAccount — mutation for `DELETE /api/v1/me/account`.
 *
 * Wired into Settings → Account. On success:
 *   - clears local JWT
 *   - sets `auth.pendingDeletion` so the splash renders the grace-mode banner
 *
 * The actual hard-delete is enqueued by the backend with a 30-day delay; this
 * mutation just confirms the soft-delete + grace window has started.
 */

import { useMutation } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

interface DeleteAccountResponse {
  ok: true;
  deletedAt: string;
  gracePeriodDays: number;
}

export function useDeleteAccount(): {
  mutate: () => void;
  mutateAsync: () => Promise<DeleteAccountResponse>;
  isPending: boolean;
  isError: boolean;
} {
  const jwt = useAuth((s) => s.jwt);
  const setPendingDeletion = useAuth((s) => s.setPendingDeletion);

  const m = useMutation<DeleteAccountResponse, Error, void>({
    mutationFn: async () => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch('/api/v1/me/account', jwt, { method: 'DELETE' });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`delete_account_failed: ${res.status} ${text}`);
      }
      return (await res.json()) as DeleteAccountResponse;
    },
    onSuccess: (data) => {
      setPendingDeletion({ deletedAt: data.deletedAt, gracePeriodDays: data.gracePeriodDays });
    },
  });

  return {
    mutate: () => m.mutate(),
    mutateAsync: () => m.mutateAsync(),
    isPending: m.isPending,
    isError: m.isError,
  };
}
